import * as React from "react";

import { Button, Stack, TextField } from "@mui/material";

import { useFormik } from "formik";
import * as Yup from "yup";

import { useGetCurrentUser, useUpdateCurrentUser } from "../../store";
import { User } from "../../types";

interface UserDetailsFormProps {
  updateText?: string;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  requireUpdate?: boolean;
}

const UserDetailsForm: React.FC<UserDetailsFormProps> = ({
  updateText = "Update Details",
  onSuccess,
  onError,
  requireUpdate = true,
}) => {
  const { data: currentUser } = useGetCurrentUser();

  const { mutate: updateUser } = useUpdateCurrentUser(onSuccess, onError);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: currentUser?.fullName || "",
      jobTitle: currentUser?.jobTitle || "",
    },
    //require length above 0
    validationSchema: Yup.object({
      fullName: Yup.string()
        .required("Full Name is required")
        .min(2, "Full Name is too short")
        .max(24, "Full Name is too long"),
      jobTitle: Yup.string()
        .required("Job Title is required")
        .max(24, "Job Title is too long"),
    }),
    onSubmit: (values) => {
      const { fullName, jobTitle } = values;

      if (!currentUser) {
        return;
      }

      if (
        fullName === currentUser.fullName &&
        jobTitle === currentUser.jobTitle
      ) {
        if (onSuccess) {
          onSuccess();
        }
        return;
      }

      const updatedUser = {
        jobTitle,
        fullName,
      };

      updateUser(updatedUser);
    },
  });

  const fullNameChanged = currentUser?.fullName !== formik.values.fullName;
  const jobTitleChanged = currentUser?.jobTitle !== formik.values.jobTitle;

  let updateDisabled = !fullNameChanged && !jobTitleChanged;

  if (!requireUpdate) {
    updateDisabled = false;
  }

  return (
    <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
      <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
        <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
          <TextField
            fullWidth
            size="small"
            id="fullName"
            name="fullName"
            label="Full Name"
            variant="outlined"
            autoComplete="fullName"
            value={formik.values.fullName}
            onChange={formik.handleChange}
            error={formik.touched.fullName && Boolean(formik.errors.fullName)}
            helperText={formik.touched.fullName && formik.errors.fullName}
          />
          <TextField
            fullWidth
            size="small"
            id="jobTitle"
            name="jobTitle"
            label="Job Title"
            variant="outlined"
            autoComplete="jobTitle"
            value={formik.values.jobTitle}
            onChange={formik.handleChange}
            error={formik.touched.jobTitle && Boolean(formik.errors.jobTitle)}
            helperText={formik.touched.jobTitle && formik.errors.jobTitle}
          />
        </Stack>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={updateDisabled}
        >
          {updateText}
        </Button>
      </Stack>
    </form>
  );
};

export default UserDetailsForm;
